import { Component, computed, inject, OnDestroy, OnInit, output, signal, ViewEncapsulation } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { DroppableEnpsComponent } from '@common/components/base/droppable-enps-component';
import { ObjectTypes } from '@common/enums/object-types';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { Record } from '@src/suite/common/data-models/records/record';
import { TreeLikeFlatNode } from '@src/suite/common/data-models/search-results/search-results';
import { SearchType } from '@src/suite/common/enums/enums';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { CastActions, CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import {
    selectCurrentPublicationList,
    selectCurrentPublicationListSectionKeys,
    selectListLoading,
    selectProcessing,
    selectSavedViewStates,
    selectSelectedSavedViewState,
    selectSideRecords,
    selectSideRecordsLoading,
    selectSideRecordsTreeDataPair,
    selectSideRecordsTreeDataSource,
} from '@src/suite/common/store/cast/cast.selectors';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectViewStateMaxPublicationRevisionNumber, selectViewStatePublicationRevisionNumber } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { ViewStateRecord } from '@suite/common/data-models/records/viewstate-record';
import { filter, map, take } from 'rxjs';

@Component({
    selector: 'st-cast-objects',
    templateUrl: './cast-objects.html',
    styleUrls: ['./cast-objects.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-cast-objects',
    },
})
export class CastObjectsComponent extends DroppableEnpsComponent implements OnDestroy, OnInit {
    public closeCastObjects = output();
    private store = inject(Store);
    private visualStateService = inject(VisualStateService);
    private modalService = inject(ModalService);

    SearchType = SearchType;

    public selectedListSearchType = signal(SearchType.SAVED);
    public searchTerm = signal('');

    languageSettings = this.store.selectSignal(selectI18N)();
    savedViewStates = this.store.selectSignal(selectSavedViewStates);
    currentViewState = this.store.selectSignal(selectSelectedSavedViewState);
    records = this.store.selectSignal(selectSideRecordsTreeDataSource);
    treeDataPair = this.store.selectSignal(selectSideRecordsTreeDataPair);
    filteredAvailableRecords = this.store.selectSignal(selectSideRecords);
    maxPublicationRevisionNumber = this.store.selectSignal(selectViewStateMaxPublicationRevisionNumber);
    publicationRevisionNumber = this.store.selectSignal(selectViewStatePublicationRevisionNumber);
    publicationList = this.store.selectSignal(selectCurrentPublicationList);
    isProcessing = this.store.selectSignal(selectProcessing);
    listLoading = this.store.selectSignal(selectListLoading);
    sections = this.store.selectSignal(selectCurrentPublicationListSectionKeys);
    loadingRecords = this.store.selectSignal(selectSideRecordsLoading);

    savedViewDropdownVisible = computed(() => this.selectedListSearchType() === SearchType.SAVED);
    disabledCastObjects = computed(() => {
        const revNumber = this.publicationRevisionNumber();
        const maxRevNumber = this.maxPublicationRevisionNumber();
        return !!(revNumber < maxRevNumber || !!this.publicationList()?.PSTPublicationListPublishedTime?.moment?.isValid()) || this.isProcessing() || this.listLoading();
    });
    showAddAllButton = computed(() => {
        return !(this.visualStateService.ConfigSettings?.HideAddClearAllCastButtons === '1');
    });

    // TODO: I thinkg I have fixed this todo:
    // // TODO: need to handle available records, based on vs.viewStateRemoveAddedRecords, and if the record is added
    // filteredAvailableRecords = computed(() => this.rawRecords());
    disabledAddAllButton = computed(() => {
        return this.disabledCastObjects() || this.filteredAvailableRecords()?.length === 0 || this.isProcessing() || this.listLoading();
    });

    ngOnInit() {
        if (!this.savedViewStates()) {
            this.store.dispatch(CastApiActions.loadViewStates());
        }
    }

    public ObjectTypesEnum = ObjectTypes;

    public selectedSavedView: ViewStateRecord = null;

    public channelSections: { [id: string]: Array<StorytellingPublicationRecord> } = {};

    public async toggleSearchTab() {
        if (this.selectedListSearchType() === SearchType.SEARCH) {
            this.store.dispatch(CastActions.clearSearchTerm());
            this.searchTerm.set('');
        }
        if (this.savedViewDropdownVisible()) {
            // if we are switching from search to saved views, and there is a saved view selected
            // we need to prefetch again
            this.searchTerm.set('');
            this.store.dispatch(CastApiActions.reloadRecordsForSelectedViewState());
        }
    }

    onViewStateGuidChanged(viewState: MatSelectChange) {
        this.store.dispatch(CastActions.setCurrentViewState({ guid: viewState.value }));
    }

    public onSearchHandler(value: string | number) {
        this.searchTerm.set(value.toString());
        this.store.dispatch(CastApiActions.searchRecords({ searchTerm: this.searchTerm() }));
    }

    public trackByGuid(index, node) {
        if (node) {
            return node?.data?.Guid;
        }
    }

    async onItemClickHandler(node: TreeLikeFlatNode<Record>) {
        const data = node?.object as StorytellingRecord;
        const nodeUuid = node?.uniqueId;
        if (data?.Type === ObjectTypes.TOPIC) {
            this.store.dispatch(OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: data?.Guid, recordType: data?.Type, treeDataPair: this.treeDataPair(), currentNodeUUID: nodeUuid }));
        }
        if (data?.Type === ObjectTypes.ASSIGNMENT) {
            this.store.dispatch(
                OpenFullscreenModalActions.openAssignmentFullscreenDialog({
                    recordGuid: data?.Guid,
                    recordType: data?.Type,
                    sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN,
                    isMetadata: true,
                    treeDataPair: this.treeDataPair(),
                    currentNodeUUID: nodeUuid,
                })
            );
        }
    }

    onDragStarted(node: TreeLikeFlatNode<Record>) {
        super.onDragStarted(node);
        this.store.dispatch(CastActions.setDragRecordGuid({ guid: node.uniqueId }));
    }

    onDragEnded() {
        super.onDragEnded();
        this.store.dispatch(CastActions.stopDrag());
    }

    addAll() {
        this.modalService
            .openModal(this.languageSettings.PSTWebConfirmAddAllItemsLabel, this.languageSettings.PSTWebConfirmAddAllItemsMessage, 'Yes', 'No')
            .result.pipe(
                take(1),
                filter(result => result === ModalResponseCodes.OK),
                map(() => {
                    // if (resultCode === ModalResponseCodes.OK) {
                    // }
                    this.store.dispatch(
                        CastApiActions.addPublicationsToPublicationListSection({
                            recordsToAdd: this.filteredAvailableRecords()
                                .map(r => r.Guid)
                                .filter(g => !!g),
                            section: this.sections()?.[0],
                        })
                    );
                })
            )
            .subscribe();
    }

    toggleCastObjectsElevation() {
        this.store.dispatch(ViewStateActions.toggleFilterElevation());
    }

    reset() {
        if (this.savedViewDropdownVisible()) {
            this.store.dispatch(CastApiActions.reloadRecordsForSelectedViewState());
        } else {
            this.store.dispatch(CastApiActions.searchRecords({ searchTerm: this.searchTerm() }));
        }
    }

    /*
    @Output() public closeCastObjects: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild(CdkVirtualScrollViewport, { static: false }) cdkVirtualScrollViewport: CdkVirtualScrollViewport;

    // public async toggleTabVisibility(savedViewDropdownVisible: boolean) {
    //     this.savedViewDropdownVisible = savedViewDropdownVisible;
    //     if (!this.savedViewDropdownVisible) {
    //         // clear available records whenever we switch to search view
    //         this.castService.availableRecords = new Array<StorytellingRecord>();
    //     } else {
    //         // if we are switching from search to saved views, and there is a saved view selected
    //         // we need to prefetch again
    //         this.searchTerm = '';
    //         this.castService.getSavedViewItems();
    //     }
    // }

    // public requestInProgress: boolean = false;
    // public savedViewDropdownVisible: boolean = true;
    // public searchTerm: string = '';
    public get disabledAddAllButton$(): Observable<boolean> {
        return this.disabledCastObjects$.pipe(
            map(value => {
                return this.castService.filteredAvailableRecords?.length === 0 || this.navigationService.currentListView.loading || this.loading || value;
            })
        );
    }

    public async findCandidates(event: any) {
        if (!this.loading) {
            const { term } = event;
            const vs: SearchViewStateRecord = new SearchViewStateRecord();
            this.searchTerm = term;
            vs.viewStateType = ViewStateTypes.SEARCH;
            vs.viewStateSearchQuery = term;
            await this.castService.getSavedViewItems(vs);
        }
    }

    destroyed$: Subject<void> = new Subject<void>();
    public _item: NavigationItem;
    item$ = this.store
        .select(selectCurrentItem)
        .pipe(
            tap(item => {
                this._item = item ? item : null;
            }),
            takeUntil(this.destroyed$)
        )
        .subscribe();

    maxPublicationRevisionNumber$ = this.store.select(selectViewStateMaxPublicationRevisionNumber);
    publicationRevisionNumber$ = this.store.select(selectViewStatePublicationRevisionNumber);

    protected _vs: ViewStateRecord;
    vs$ = this.store
        .select(selectCurrentViewState)
        .pipe(
            tap(vs => {
                this._vs = vs ? vs : null;
            }),
            takeUntil(this.destroyed$)
        )
        .subscribe();

    constructor(
        public appService: AppService,
        public navigationService: NavigationService,
        public layoutState: LayoutState,
        public castService: CastService,
        public changeDetectorRef: ChangeDetectorRef,
        public playbookSearchService: PlaybookSearchService,
        public visualStateService: VisualStateService,
        public globalTablesService: GlobalTablesService,
        public publicationChannelsService: PublicationChannelsService,
        public playbookService: PlaybookService
    ) {
        super();
        this.init();
    }

    // public async onViewStateGuidChanged() {
    //     this.store.dispatch(ViewStateActions.updateViewStateViewStateGuid({ guid: this.castService.viewStateGuid }));
    //     this.castService.getSavedViewItems();
    // }

    ngOnDestroy() {
        super.ngOnDestroy();
        DataStore.unregisterRTUListener('cast-list-rtu-listener');
    }

    areDatesTheSame(d1: Date, d2: Date) {
        return d1.getUTCFullYear() === d2.getUTCFullYear() && d1.getUTCMonth() === d2.getUTCMonth() && d1.getUTCDate() === d2.getUTCDate();
    }

    public transformRecord(item: StorytellingRecord): StorytellingPublicationRecord {
        // TODO: CHeck this
        // need to transform from a source record item - such as a Topic or Assignment, to the target Publication Record
        const newPublication: StorytellingPublicationRecord = initializeRecord(ObjectTypesEnum.PUBLICATION);
        newPublication.Title = item.Title;
        newPublication.linkedRecords = [...newPublication.linkedRecords, item];
        return newPublication;
    }

    public get searchResults(): CastSearchResults {
        return this.castService.castSearchResults;
    }

    public get channel(): StorytellingPublicationChannelRecord {
        return (<PublicationViewStateRecord>this._vs).publicationChannel;
    }

    public get sections(): Array<string> {
        // TODO FIX THIS
        if (this.castService.castSearchResults.publicationList && this.castService.castSearchResults.publicationList.PublicationSequenceCollection !== undefined) {
            // return this.castService.castSearchResults.publicationList.publicationListSections;
            return [];
        }
        // return (<PublicationViewStateRecord>this._vs).publicationChannel?.PSTPublicationChannelSections;
        return [];
    }

    get disabledCastObjects$(): Observable<boolean> {
        return combineLatest([this.publicationRevisionNumber$, this.maxPublicationRevisionNumber$]).pipe(
            map(([revNumber, maxRevNumber]) => {
                return !!(revNumber < maxRevNumber || this.searchResults?.publicationList?.PSTPublicationListPublishedTime || this.castService.isCreatePublicationPending);
                return true;
            })
        );
    }

    private async init() {
        // dispatch action
        // await this.publicationChannelsService.getPublicationChannelRecords();
        // this.store.dispatch(CastApiActions.loadPublicationsChannels());
    }

    public async onViewStateRecordChange(vs: ViewStateRecordsAlias) {
        try {
            if (!vs) return;
            if (this._vs instanceof PublicationViewStateRecord) {
                this.castService.viewStateGuid = this._vs.viewStateGUID;

                if (!this.castService.viewStateGuid && this.castService.playbookViewStateRecords.length > 0) {
                    this.castService.viewStateGuid = this.castService.playbookViewStateRecords[0].guid;
                }
            }
            await this.reInitializeView(vs);
        } catch (e) {
            console.log(e);
        }
    }

    public async reInitializeView(vs?: ViewStateRecordsAlias) {
        if (!vs) {
            vs = this._vs;
        }

        if (vs && vs instanceof PublicationViewStateRecord) {
            vs.maxPublicationRevisionNumber = -1;
            await this.layoutState.getViewStateRecords('playbook', undefined, this.globalTablesService.PlaybookTeamsArray.find(x => x.ID === this.appService.nomWebServicesJson.PSTGroup).ID);
            await this.castService.getSavedViewItems();

            this.store.dispatch(PlaybookApiActions.loadTopicTemplates());
        }
    }

    public async onViewStateRecordPropertyChange(prop: [string, any, any]) {
        if (!this._vs || !(this._vs instanceof PublicationViewStateRecord)) {
            return;
        }

        if (this.navigationService.isNewView(this._item)) {
            return;
        }

        const field = prop[0];
        const value = prop[1];
        const prev = prop[2];
        if (field === PSTFieldNames.PSTPublicationChannelGUID && prev !== value) {
            await this.reInitializeView();
        }

        if (field === PSTFieldNames.PSTPublicationListEditionName && prev !== value) {
            await this.reInitializeView();
        }

        if (field === PSTFieldNames.PSTViewStateFilter) {
            await this.reInitializeView();
        }

        if (field === PSTFieldNames.PSTViewStateGUID) {
            if (prev !== value) {
                await this.castService.getSavedViewItems();
            }
        }
        if (this.cdkVirtualScrollViewport) {
            this.cdkVirtualScrollViewport.checkViewportSize();
        }
    }

    private _editionNameTimeout: any;
    public updateEditionName(event: any) {
        clearTimeout(this._editionNameTimeout);
        this._editionNameTimeout = setTimeout(() => {
            (<PublicationViewStateRecord>this._vs).editionName = event.target.value;
        }, 500);
    }

    private _publicationListTitleTimeout: any;
    public updatePublicationListTitle(event: any) {
        clearTimeout(this._publicationListTitleTimeout);
        this._publicationListTitleTimeout = setTimeout(() => {
            (<PublicationViewStateRecord>this._vs).publicationListTitle = event.target.value;
        }, 500);
    }

    public async removeAll() {
        if (this.requestInProgress) {
            return;
        }
        try {
            this.requestInProgress = true;
            let guids = [];
            for (const section of this.sections) {
                const channelGuids = this.castService.channelSections[section].map(rec => rec.Guid);
                guids = [...guids, ...channelGuids];
            }

            const deletePublicationsArgsList = [];
            const publicationArgs = {
                publicationList: this.castService.castSearchResults.publicationList,
                section: '',
                index: 0,
                channelSections: null,
            };
            deletePublicationsArgsList.push(publicationArgs);
            const resp = await this.castService.deletePublicationRecords(deletePublicationsArgsList);
            if (resp && resp.status) {
                // TODO FIX THIS
                // if (this.castService.castSearchResults.publicationList) this.castService.castSearchResults.publicationList.removeChildren();
                // const restrictedPubList = this.searchResults.publicationListsOfRestrictedChannelTypes.find(list => list.guid === this.castService.castSearchResults.publicationList.guid);
                // if (restrictedPubList) {
                //     restrictedPubList.removeChildren();
                // }
                // guids.forEach(guid => {
                //     const currentRecord: StorytellingPublicationRecord = DataStore.byGuid<StorytellingPublicationRecord>(guid, '');
                //     if (currentRecord) {
                //         DataStore.disposeRecord(currentRecord, true);
                //     }
                // });
            }

            this.requestInProgress = false;
        } catch (e) {
            console.log(e);
        }
    }

    public async addAll() {
        const modalRef = this.castService.modalService.openModal(this.languageSettings.PSTWebConfirmAddAllItemsLabel, this.languageSettings.PSTWebConfirmAddAllItemsMessage, 'Yes', 'No');
        let modalRes;
        try {
            modalRes = await firstValueFrom(modalRef.result);
        } catch (er) {
            modalRes = ModalResponseCodes.CANCEL;
        }
        if (modalRes === ModalResponseCodes.OK) {
            this.navigationService.currentListView.showLoader();
            try {
                // adds all available records to the first section.
                // if any of the records is already there, it's skipped
                // if there are no sections in the view, we exit.
                // TODO: this should be refactored to handle multiple sections
                if (
                    !this.channel ||
                    !this.channel.PSTPublicationChannelSections ||
                    this.channel.PSTPublicationChannelSections.length === 0 ||
                    !this.castService.channelSections ||
                    !this.sections ||
                    this.sections.length === 0
                ) {
                    this.navigationService.currentListView.hideLoader();
                    return;
                }
                let publicationListGuid: string = null;
                if (this.castService.castSearchResults.publicationList === null) {
                    const result = await this.castService.createPublicationList(this._vs, this.sections, this.castService.channelSections);
                    // await this.loadPublicationList(true);
                    if (result.status) {
                        publicationListGuid = result.data.Guid;
                    }
                } else {
                    publicationListGuid = this.castService.castSearchResults.publicationList.Guid;
                }

                // determine which available records are already there
                const recordsToAdd: Array<StorytellingRecord> = new Array<StorytellingRecord>();
                for (const rec of this.castService.filteredAvailableRecords) {
                    // let addRecord = await this.handleRestrictedPublicationChannelTypes(rec);
                    await new Promise(r => setTimeout(r, 20));
                    // if (!addRecord) continue;
                    let present: boolean = false;
                    for (const pub of this.castService.channelSections[this.sections[0]]) {
                        if (pub.linkedRecords?.[0].Guid === rec.Guid) {
                            present = true;
                            break;
                        }
                    }
                    if (!present) {
                        recordsToAdd.push(rec);
                    }
                }
                const newPublications: Array<StorytellingPublicationRecord> = new Array<StorytellingPublicationRecord>();
                // const createdPublications: Array<PublicationRecord> = new Array<PublicationRecord>();
                for (let i = 0; i < recordsToAdd.length; i++) {
                    const newPublication: StorytellingPublicationRecord = initializeRecord(ObjectTypesEnum.PUBLICATION);
                    newPublication.Title = recordsToAdd[i].Title;
                    newPublication.linkedRecords = [...newPublication.linkedRecords, recordsToAdd[i]];
                    newPublications.push(newPublication);
                }
                const createdPublicationsResp: ServiceResponse<Array<StorytellingPublicationRecord>> = await this.castService.createPublications(
                    newPublications,
                    this.sections[0],
                    this.castService.channelSections[this.sections[0]].length,
                    [],
                    publicationListGuid
                );
                for (const pub of createdPublicationsResp.data) {
                    this.castService.channelSections[this.sections[0]].push(pub);
                }
            } catch (e) {
                this.navigationService.currentListView.hideLoader();
            }
            this.navigationService.currentListView.hideLoader();
        }
    }

    toggleCastObjectsElevation() {
        if (this._vs.viewStateFilterElevation === 'over') {
            this._vs.viewStateFilterElevation = 'side';
        } else {
            this._vs.viewStateFilterElevation = 'over';
        }
    }

    async reset() {
        if (this.savedViewDropdownVisible) {
            this.castService.getSavedViewItems();
        } else {
            const vs: SearchViewStateRecord = new SearchViewStateRecord();
            vs.viewStateType = ViewStateTypes.SEARCH;
            // vs.viewStateSearchQuery = this.searchTerm;
            await this.castService.getSavedViewItems(vs);
        }
    }

    public getTopicIcon(record: StorytellingRecord) {
        if (record?.Type === ObjectTypesEnum.TOPIC) {
            return 'text_snippet';
        }
    }

    public getAssignmentIcon(record: StorytellingRecord) {
        if (record.Type === ObjectTypesEnum.ASSIGNMENT) {
            return record;
        }
    }

    public getitemCreator(record: StorytellingRecord) {
        return `${this.languageSettings.PSTWebCastObjectsCreatedBy} ${record.Creator}`;
    }

    public onCandidateDragStarted(guid: string) {
        this.castService.dragStarted = true;
        this.castService.appendOnly = true;
        this.castService.draggedGuid = guid;
    }

    public onDragEnded() {
        super.onDragEnded();

        this.castService.dragStarted = false;
        this.castService.appendOnly = false;
    }

    async onItemClickHandler(data: StorytellingRecord) {
        if (data?.Type === ObjectTypesEnum.TOPIC) {
            this.store.dispatch(OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: data?.Guid, recordType: data?.Type }));
        }
        if (data?.Type === ObjectTypesEnum.ASSIGNMENT) {
            this.store.dispatch(OpenFullscreenModalActions.openAssignmentFullscreenDialog({ recordGuid: data?.Guid, recordType: data?.Type, sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN }));
        }
    }

    public trackByGuid(index, node) {
        if (node) {
            return node?.data?.Guid;
        }
    }
        */
}
